export const NOTE_SYMBOLS = ["◇", "○", "△", "▽", "□", "↺", "③", "⑤"] as const;
export const NOTE_GRID_COLS = 2;
export const NOTE_GRID_ROWS = 4;

export interface NoteControllerState {
  readonly cursorIndex: number;
  readonly isEditing: boolean;
  readonly notes: Readonly<Record<string, string | null>>;
}

export class NoteController {
  private state: NoteControllerState;

  constructor(initialNotes: Readonly<Record<string, string | null>>) {
    this.state = { cursorIndex: 0, isEditing: false, notes: { ...initialNotes } };
  }

  getState(): NoteControllerState {
    return this.state;
  }

  moveUp(): void {
    const newIndex = this.state.cursorIndex - NOTE_GRID_COLS;
    this.state = {
      ...this.state,
      cursorIndex: newIndex < 0 ? newIndex + NOTE_SYMBOLS.length : newIndex,
    };
  }

  moveDown(): void {
    const newIndex = this.state.cursorIndex + NOTE_GRID_COLS;
    this.state = {
      ...this.state,
      cursorIndex: newIndex >= NOTE_SYMBOLS.length ? newIndex - NOTE_SYMBOLS.length : newIndex,
    };
  }

  moveLeft(): void {
    const col = this.state.cursorIndex % NOTE_GRID_COLS;
    const newIndex = col === 0
      ? this.state.cursorIndex + (NOTE_GRID_COLS - 1)
      : this.state.cursorIndex - 1;
    this.state = { ...this.state, cursorIndex: newIndex };
  }

  moveRight(): void {
    const col = this.state.cursorIndex % NOTE_GRID_COLS;
    const newIndex = col === NOTE_GRID_COLS - 1
      ? this.state.cursorIndex - (NOTE_GRID_COLS - 1)
      : this.state.cursorIndex + 1;
    this.state = { ...this.state, cursorIndex: newIndex };
  }

  enterEditMode(): void {
    this.state = { ...this.state, isEditing: true };
  }

  exitEditMode(): void {
    this.state = { ...this.state, isEditing: false };
  }

  recordChar(char: string): void {
    const symbol = this.getCurrentSymbol();
    this.state = {
      ...this.state,
      notes: { ...this.state.notes, [symbol]: char },
    };
  }

  deleteChar(): void {
    const symbol = this.getCurrentSymbol();
    this.state = {
      ...this.state,
      notes: { ...this.state.notes, [symbol]: null },
    };
  }

  getCurrentNote(): string | null {
    return this.state.notes[this.getCurrentSymbol()] ?? null;
  }

  getSymbolNote(symbol: string): string | null {
    return this.state.notes[symbol] ?? null;
  }

  getAllNotes(): Readonly<Record<string, string | null>> {
    return this.state.notes;
  }

  getCurrentSymbol(): string {
    return NOTE_SYMBOLS[this.state.cursorIndex]!;
  }
}
